"use client";
import React, { useState } from "react";
import ManagementHeader from "../../../components/ManagementHeader";

export default function ManagementEmail() {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");

  const [attachments, setAttachments] = useState([]);
  const [attachmentName, setAttachmentName] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const addAttachment = () => {
    if (!attachmentName || !attachmentUrl) return;

    setAttachments((prev) => [
      ...prev,
      { name: attachmentName, url: attachmentUrl },
    ]);

    setAttachmentName("");
    setAttachmentUrl("");
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch("/api/sendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to,
          subject,
          content,
          attachments,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to send email");
      }

      setMessage({
        type: "success",
        text: "Email sent successfully.",
      });

      setTo("");
      setSubject("");
      setContent("");
      setAttachments([]);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <ManagementHeader />

      <div className="nhsuk-width-container mt-6">
        <h1 className="nhsuk-heading-l">Management Email Portal</h1>

        {message && (
          <div
            className={`nhsuk-inset-text ${
              message.type === "success" ? "border-green-600" : "border-red-600"
            }`}
          >
            <span className="nhsuk-u-visually-hidden">
              {message.type === "success" ? "Success" : "Error"}:
            </span>
            <p>{message.text}</p>
          </div>
        )}

        <div className="nhsuk-grid-row">
          {/* ================= LEFT FORM ================= */}
          <div className="nhsuk-grid-column-two-thirds">
            <div className="nhsuk-form-group">
              <label className="nhsuk-label">Recipient Email(s)</label>
              <input
                className="nhsuk-input"
                type="text"
                placeholder="user@example.com, other@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label">Subject</label>
              <input
                className="nhsuk-input"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label">Email Content</label>
              <textarea
                className="nhsuk-textarea"
                rows={8}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            {/* ================= ATTACHMENTS ================= */}
            <div className="nhsuk-form-group">
              <label className="nhsuk-label">Attachments (URL-based)</label>

              <div className="flex gap-2 mb-2">
                <input
                  className="nhsuk-input"
                  type="text"
                  placeholder="File name"
                  value={attachmentName}
                  onChange={(e) => setAttachmentName(e.target.value)}
                />
                <input
                  className="nhsuk-input"
                  type="text"
                  placeholder="Public file URL"
                  value={attachmentUrl}
                  onChange={(e) => setAttachmentUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="nhsuk-button nhsuk-button--secondary"
                  onClick={addAttachment}
                >
                  Add
                </button>
              </div>

              {attachments.length > 0 && (
                <ul className="nhsuk-list nhsuk-list--bullet">
                  {attachments.map((file, idx) => (
                    <li key={idx}>
                      {file.name}
                      <button
                        className="nhsuk-link ml-2 text-red-600"
                        onClick={() => removeAttachment(idx)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              className="nhsuk-button"
              onClick={handleSend}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Email"}
            </button>
          </div>

          {/* ================= PREVIEW ================= */}
          <div className="nhsuk-grid-column-one-third">
            <div className="nhsuk-card">
              <div className="nhsuk-card__content">
                <h3 className="nhsuk-card__heading">Preview</h3>

                <p>
                  <strong>To:</strong> {to || "—"}
                </p>
                <p>
                  <strong>Subject:</strong> {subject || "—"}
                </p>

                <hr />

                <p style={{ whiteSpace: "pre-wrap" }}>
                  {content || "Email content preview…"}
                </p>

                {attachments.length > 0 && (
                  <>
                    <hr />
                    <p>
                      <strong>Attachments:</strong>
                    </p>
                    <ul className="nhsuk-list nhsuk-list--bullet">
                      {attachments.map((a, i) => (
                        <li key={i}>{a.name}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
